import React, {useEffect, useState} from 'react';

import {DocRow, DocTable, DocTitle} from '../../components';
import {DocumentsBatch, EGDResponse} from '../../types';

import {
    DATA_NOT_AVAILABLE,
    defaultTo,
    getAddress,
    getDocsData,
    getFeatures,
    getIsCommonFlats,
    getOwnRooms,
    getPrevAddress,
    isNotEmpty,
    mayBe,
    prepareCommonRoomsTable,
    prepareOwnersTableData,
    preparePrebPeopleTableData,
    preparePrefTableData,
    prepareRegPeopleTableData,
} from './utils';
import {selectedDocument} from "./index";
import {AvailableDocuments} from "./AvailableDocuments";
import {QRFooter} from "../qr_footer/QRFooter";



export const EgdDoc: React.FC<{ documents: DocumentsBatch, withVersions?: boolean }> = ({
                                                                                            documents,
                                                                                            withVersions = true
                                                                                        }) => {
    const [selectedDocument, setSelectedDocument] = useState<selectedDocument>(
            {
                document: documents.documents[0],
                documentTypeName: documents.documentsType
            });

    const [doc, setDocument] = useState<EGDResponse>(
            selectedDocument.document.document as EGDResponse
    )

    useEffect(() => {
        setDocument(selectedDocument.document.document as EGDResponse);
    }, [selectedDocument.document.version]);


    const {
        blockchainUid,
        people,
        data,
        partTypeList,
        owners,
        docs,
        prefList,
        rooms,
        flsMan,
    } = doc;

    const address = getAddress(data);
    const prevAddress = getPrevAddress(data);
    const ownRooms = getOwnRooms(rooms.room, data.flsId);

    const isCommonFlats = getIsCommonFlats(data);

    const subTitle = `по состоянию на ${mayBe(data.currdate)}, за период с ${defaultTo(
            data.sdd,
            '"___"_________ 20__г.'
    )} по ${defaultTo(data.fdd, '"___"_________ 20__г.')}`;

    const ownersTableData = prepareOwnersTableData(owners.owner);
    const regPeopleTableData = prepareRegPeopleTableData(people.man, data.dopAttr);
    const prebPeopleTableData = preparePrebPeopleTableData(people.man);
    const prefsTableData = preparePrefTableData(prefList);
    const commonRoomsTableData = prepareCommonRoomsTable({rooms: rooms.room, id: data.flsId, flsMan});
    const docsData = getDocsData(docs);
    const features = getFeatures(data);

    return (
            <>
                <div className="col-span-12 sm:col-span-12">
                    <DocTitle uid={blockchainUid} title="Единый жилищный документ" subtitle={subTitle}/>
                </div>

                <div className="col-span-12 sm:col-span-8">
                    <DocRow title="ФИО заявителя" value={mayBe(data.zayavMan)}/>
                    <DocRow title="Телефон заявителя" value={data.phone}/>
                    <DocRow title="Паспортные данные" value={mayBe(data.pass)}/>
                    {data.currDocZayavitel !== 1 && (
                            <>
                                <DocRow title="Сведения о ранее выданном паспорте" value={mayBe(data.prevPass)}/>
                                <DocRow title="Причина замены паспорта" value={mayBe(data.changeReason)}/>
                            </>
                    )}
                    <DocRow
                            title={
                                data.zayavOwnNoreg !== 1
                                        ? 'Заявитель зарегистрирован по месту жительства'
                                        : 'Заявитель является собственником жилого помещения без регистрации по адресу'
                            }
                            value={address}
                    />
                    {prevAddress && <DocRow title="Прежнее наименование адреса" value={prevAddress}/>}
                    <DocRow
                            title="Управляющая компания"
                            value={mayBe([data.serviceCompany, data.adrTelCompany].join(', '))}
                    />
                    <DocRow
                            title="Вид заселения"
                            value={isNotEmpty(partTypeList.partType[0]) ? mayBe(partTypeList.partType.join(', ')) : DATA_NOT_AVAILABLE}
                    />
                    <DocRow title="Лицевой счет/карточка учета" value={mayBe(data.flsId)}/>
                    <DocRow title="На кого открыт Лицевой счет/карточка учета" value={mayBe(data.oksDecl)}/>
                    <DocRow title="Собственники" value={mayBe(data.owners)}/>
                    <DocRow title="На основании" usePre={true} value={docsData.map(d => d.toString()).join('\n')}/>
                </div>

                {
                        withVersions && documents.documents.length > 0 &&
                        <div className="col-span-12 sm:col-end-13 sm:col-span-3">
                            <AvailableDocuments
                                    docs={documents}
                                    setSelectedDocument={setSelectedDocument}
                                    className={'-mt-12'}
                            />
                        </div>
                }
                <div className="col-span-12 sm:col-span-8">
                    <DocTable {...ownersTableData} />

                    <DocRow title="Характеристика занимаемого жилого помещения" value={features}/>
                    <DocRow title="Площадь жилого помещения" value={mayBe(data.sAll, ' кв.м.')}/>
                    <DocRow
                            title="Площадь балконов, лоджий, веранд, террас"
                            value={mayBe(data.sLet, ' кв.м.')}
                    />
                    <DocRow title="Общая площадь жилого помещения" value={mayBe(data.sAllLet, ' кв.м.')}/>
                    {isCommonFlats && (
                            <DocRow
                                    title="Приведенная доля общей площади жилого помещения"
                                    value={mayBe(data.sPriv, ' кв.м.')}
                            />
                    )}
                    <DocRow title="Жилая площадь" value={mayBe(data.sGil, ' кв.м.')}/>
                    <DocRow title="Площадь кухни" value={mayBe(data.sKitchen, ' кв.м.')}/>
                    <DocRow title="Санузел" value={mayBe(data.sanType)}/>
                    <DocRow title="Количество комнат в квартире" value={mayBe(data.croomFlat)}/>
                    <DocRow
                            title="Занимаемые комнаты, площадь (жилая)"
                            value={mayBe(data.flsGilSqr, ' кв.м.')}
                    />
                    <DocRow
                            title="Занимаемые комнаты, площадь (общая)"
                            value={mayBe(data.flsAllSqr, ' кв.м.')}
                    />
                    <DocRow title="Занимаемые комнаты" usePre={true} value={ownRooms}/>
                    <DocRow title="Этаж жилого помещения" value={mayBe(data.floor)}/>
                    <DocRow title="Этажей в доме" value={mayBe(data.cfloor)}/>
                    <DocRow title="Материал стен" value={mayBe(data.materialSten)}/>
                    <DocRow title="Год постройки" value={mayBe(data.houseYear)}/>
                    <DocRow title="Серия" value={mayBe(data.seria)}/>
                    <DocRow title="Лифт" value={mayBe(data.lift)}/>
                    <DocRow title="Центральное отопление" value={mayBe(data.centrOtopl)}/>
                    <DocRow title="Водопровод" value={mayBe(data.cw)}/>
                    <DocRow title="Горячее водоснабжение" value={mayBe(data.hw)}/>
                    <DocRow title="Тип системы ГВС" value={mayBe(data.schemaHw)}/>
                    <DocRow title="Электроснабжение" value={mayBe(data.electr)}/>
                    <DocRow title="Ванна" value={mayBe(data.vanna)}/>
                    <DocRow title="Газоснабжение" value={mayBe(data.gas)}/>
                    <DocRow title="Канализация" value={mayBe(data.kanal)}/>

                    <DocTable {...regPeopleTableData} />
                    <DocTable {...prebPeopleTableData} />
                    <DocTable {...prefsTableData} />

                    <DocRow title="Субсидия" value={mayBe(data.subsStatus)}/>
                    <DocRow title="Наличие приборов учета" value={mayBe(data.counterType)}/>
                    <DocRow title="Задолженность по оплате коммунальных услуг" value={mayBe(data.dolgStatus)}/>

                    <DocRow
                            title="Еще проживает семей в квартире (для квартир коммунального заселения)"
                            value={isCommonFlats ? mayBe(data.cfls) : '______'}
                    />

                    {isCommonFlats ? <DocTable {...commonRoomsTableData} /> : null}

                </div>
                <div className="col-span-12 md:col-span-8 md:order-last">
                    <QRFooter blockchainUid={doc.blockchainUid}
                              refDate={doc.data.fd}
                              mfcInfoName={doc.data.firmName}
                              mfcUserName={doc.data.userName}
                    />
                </div>
            </>
    );
};
